import { useMemo } from 'react';
import {
  metricThresholdsExecutionRuns,
  metricThresholdsPipelines,
  metricThresholdsSuites,
  metricThresholdsTests,
} from '@/shared/utils/metricsThresholds';
import { useProjectByIdQuery } from './projects';
import type { MetricThresholdGroup, ProjectMetricThresholds } from '@flowqa/shared';

const defaultThresholds: ProjectMetricThresholds = {
  suites: metricThresholdsSuites,
  executionRuns: metricThresholdsExecutionRuns,
  tests: metricThresholdsTests,
  pipelines: metricThresholdsPipelines,
};

const mergeThresholdGroup = (
  base: MetricThresholdGroup,
  update?: Partial<MetricThresholdGroup>,
): MetricThresholdGroup => ({
  passRate: { ...base.passRate, ...(update?.passRate ?? {}) },
  frequency: { ...base.frequency, ...(update?.frequency ?? {}) },
  avgDuration: { ...base.avgDuration, ...(update?.avgDuration ?? {}) },
  flakeScore: { ...base.flakeScore, ...(update?.flakeScore ?? {}) },
  mttf: { ...base.mttf, ...(update?.mttf ?? {}) },
  greenTime: { ...base.greenTime, ...(update?.greenTime ?? {}) },
});

const mergeProjectThresholds = (
  source?: Partial<ProjectMetricThresholds>,
): ProjectMetricThresholds => ({
  suites: mergeThresholdGroup(defaultThresholds.suites, source?.suites),
  executionRuns: mergeThresholdGroup(defaultThresholds.executionRuns, source?.executionRuns),
  tests: mergeThresholdGroup(defaultThresholds.tests, source?.tests),
  pipelines: mergeThresholdGroup(defaultThresholds.pipelines, source?.pipelines),
});

export const useProjectMetricThresholds = (
  projectId: string | null,
  queryOptions?: { staleTimeMs?: number; refetchOnWindowFocus?: boolean },
): { thresholds: ProjectMetricThresholds; isLoading: boolean } => {
  const projectQuery = useProjectByIdQuery(projectId, queryOptions);
  const thresholds = useMemo(
    () => mergeProjectThresholds(projectQuery.data?.metricThresholds),
    [projectQuery.data?.metricThresholds],
  );
  return { thresholds, isLoading: projectQuery.isLoading };
};
